export const ORIGIN_VALUES = [
	'plugin',
	'ci',
	'standalone_report',
	'marketing',
	'direct',
	'other',
] as const

export type Origin = (typeof ORIGIN_VALUES)[number]

export const STANDALONE_REPORT_PROMO_CAMPAIGN = 'standalone_report_promo'

type UtmStashKey = 'utmSource' | 'utmMedium' | 'utmCampaign' | 'utmContent' | 'utmTerm'

export type AttributionStash = Partial<Record<UtmStashKey, string>> & {
	referrer?: string
	cameFromIde?: boolean
}

/**
 * Single source of truth for the utm dimensions: query param name, stash key, and the
 * PostHog `initial_utm_*` person-property name. Add/remove a dimension here only.
 */
export const UTM_DIMENSIONS = [
	{ param: 'utm_source', key: 'utmSource', personProp: 'initial_utm_source' },
	{ param: 'utm_medium', key: 'utmMedium', personProp: 'initial_utm_medium' },
	{ param: 'utm_campaign', key: 'utmCampaign', personProp: 'initial_utm_campaign' },
	{ param: 'utm_content', key: 'utmContent', personProp: 'initial_utm_content' },
	{ param: 'utm_term', key: 'utmTerm', personProp: 'initial_utm_term' },
] as const satisfies ReadonlyArray<{ param: string; key: UtmStashKey; personProp: string }>

export function hasAnyUtm(stash: AttributionStash): boolean {
	return UTM_DIMENSIONS.some((dimension) => Boolean(stash[dimension.key]))
}

export function computeOrigin(stash: AttributionStash): Origin {
	if (stash.utmCampaign === STANDALONE_REPORT_PROMO_CAMPAIGN) {
		return 'standalone_report'
	}
	if (hasAnyUtm(stash)) {
		return 'marketing'
	}
	if (stash.cameFromIde) {
		return 'plugin'
	}
	return 'direct'
}

/** Maps a stash to PostHog `initial_utm_*` / `initial_referrer` person properties. */
export function buildInitialUtmProperties(stash: AttributionStash): Record<string, string> {
	const properties: Record<string, string> = {}
	for (const dimension of UTM_DIMENSIONS) {
		const value = stash[dimension.key]
		if (value) {
			properties[dimension.personProp] = value
		}
	}
	if (stash.referrer) {
		properties.initial_referrer = stash.referrer
	}
	return properties
}
